import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { emberApi, type TreeNode } from '../api';
import { BanyanScene, pickArrivalSpot, type Arrival } from './BanyanScene';
import { track } from '../../../../../shared-web/track';
import './ResultCeremony.css';

/** The scene settles before anything happens. */
const HOLD_MS = 400;
/** The teller speaking — the bubble bobs, the head wags and the light gathers
    inside the bubble (1.8s, matching the CSS) — then a short pause with the
    light resting there. */
const SPEAK_MS = 1800 + 400;
/** The spark's flight (matches the SMIL `dur` in BanyanScene). */
const FLIGHT_MS = 1500;
/** The new light stands lit before the paper starts to open. */
const LIT_MS = 900;
/** The paper opening from the top (matches the CSS transition). */
const UNROLL_MS = 900;
/** How long the lit tree will wait for the page under it to have a title. */
const READY_WAIT_MS = 6000;

/**
 * The post-telling ceremony (v4.0 §4.17): for a few seconds the whole screen
 * is the first screen again — the tree at dusk, the teller's back under it,
 * nothing standing on the ground. The teller is still speaking (a speech
 * bubble at their mouth, their head wagging, a light gathering in the bubble),
 * then a breath, the bubble bursts, and the
 * light climbs, curves over into the canopy and stands there
 * lit, ringed as theirs. Then the page unrolls from the top over it: the
 * Ember is the paper under the tree. The Ember is being written underneath all the while; neither
 * waits for the other. Where the telling really stood is the seals' business,
 * under the title — the ceremony only says "yours is a light on this tree".
 */
export function ResultCeremony({ ready = true, onDone }: {
  /** The page under the paper has something to show (a title, at least). Until
      it does the lit tree holds, up to READY_WAIT_MS — the light landing and
      then a blank draft with a placeholder cover was a drop, not an arrival. */
  ready?: boolean;
  onDone: () => void;
}): ReactElement {
  const [animal, setAnimal] = useState<string | null>(null);
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [wide, setWide] = useState(false);
  const [stage, setStage] = useState<Arrival['stage']>('hold');
  const [landed, setLanded] = useState(false);
  const [waitOver, setWaitOver] = useState(false);
  const [unrolling, setUnrolling] = useState(false);
  // Chosen once, before the window is known: a spot in the canopy is right
  // whichever lights turn out to be around it (the fetch below only paints).
  const [spot] = useState(() => pickArrivalSpot([], window.innerWidth >= 800));
  const arrival = useMemo<Arrival>(() => ({ ...spot, stage }), [spot, stage]);

  useEffect(() => {
    let alive = true;
    emberApi.treeYou().then((r) => { if (alive) setAnimal(r.animal); }).catch(() => {});
    emberApi.treeView({ size: window.innerWidth < 560 ? 26 : 40 }).then((v) => { if (alive) setNodes(v.nodes); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const check = (): void => setWide(window.innerWidth >= 800);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // The ceremony runs on one clock from mount; the page re-rendering
  // underneath (the draft streaming in) must not restart it.
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Reduced motion: no speaking, no flight — the light is simply lit.
    const litAt = still ? HOLD_MS : HOLD_MS + SPEAK_MS + FLIGHT_MS;
    const timers = [
      ...(still ? [] : [
        window.setTimeout(() => setStage('speaking'), HOLD_MS),
        window.setTimeout(() => setStage('flying'), HOLD_MS + SPEAK_MS),
      ]),
      window.setTimeout(() => setStage('lit'), litAt),
      window.setTimeout(() => setLanded(true), litAt + LIT_MS),
      window.setTimeout(() => setWaitOver(true), litAt + LIT_MS + READY_WAIT_MS),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, []);
  // The paper opens once the light has stood a moment AND the page under it
  // is ready (or has kept us waiting long enough).
  useEffect(() => {
    if (landed && (ready || waitOver)) setUnrolling(true);
  }, [landed, ready, waitOver]);
  // One opening, one done. Its own effect: keyed on `unrolling` alone, so the
  // re-render that starts the opening cannot clear the timer that ends it
  // (2026-09-06: it did, and the ceremony stayed mounted under the page,
  // clipped away but animating, for the whole read).
  useEffect(() => {
    if (!unrolling) return undefined;
    const t = window.setTimeout(() => {
      // The paper has opened: the ceremony was watched to its end.
      track('ui.seen', { name: 'result_ceremony', state: 'done' });
      done.current();
    }, UNROLL_MS);
    return () => window.clearTimeout(t);
  }, [unrolling]);
  // Mounting IS the just-told arrival (only ?writing=1 runs the ceremony), so
  // this is the one row that says a Result was reached straight from a
  // telling; the server's page.viewed cannot see the query.
  useEffect(() => { track('ui.seen', { name: 'result_ceremony', state: 'start' }); }, []);

  return (
    <div className={`result-ceremony${unrolling ? ' is-unrolling' : ''}`} aria-hidden>
      {/* The window slides down while the paper inside slides up by the same
          amount: the tree stays put and is uncovered from the top. Two
          transforms, so the opening runs on the compositor with no repaint
          (a clip-path transition redrew the whole scene every frame). */}
      <div className="result-ceremony-paper">
        <div className="result-ceremony-scene">
          <BanyanScene nodes={nodes} animal={animal} arrival={arrival} pannable={false} wide={wide} />
        </div>
        <div className="result-ceremony-ground banyan-ground" />
      </div>
    </div>
  );
}
